import * as React from "react";
import {
  makeStyles,
  useTheme,
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  FormControl,
  CardActions,
  FormControlLabel,
  Checkbox,
  SvgIcon,
} from "@material-ui/core";
import ClearRoundedIcon from "@material-ui/icons/ClearRounded";
import CheckMark from "./CheckMark";
import { AddCircle } from "@material-ui/icons";
import { Theme } from "../.themes";
import { IPaymentOption, IPaymentActionData } from "../store/PaymentType";
import clsx from "clsx";
import { BankAccountInputsSection } from "./BankAccountInputsSection";
import DropdownSelectorBase from "./DropdownSelectorBase";
import { useLocalValidation } from "../hooks/useLocalValidation";
import { RadioButtonGroupBase } from "./RadioButtonGroupBase";

const useStyles = makeStyles((theme: Theme) => ({
  label: {
    ...theme.paymentOption?.label,
  },
  dropdownLabel: {
    ...theme.paymentOption?.label,
    marginTop: 24,
  },
  errorMessage: {
    color: theme.palette.error.main,
  },
  card: {
    border: `${theme.border} ${theme.coverageSelect?.root?.borderColor ?? theme.palette.grey[400]}`,
    borderRadius: theme.shape.borderRadius,
    position: "relative",
    width: "100%",
    backgroundColor: theme.palette.background.default,
    ...theme.payment,
  },
  cardSelected: {
    border: `${theme.border}`,
    borderColor: theme.palette.primary.main,
    borderRadius: theme.shape.borderRadius,
    position: "relative",
    width: "100%",
    backgroundColor: theme.addOnCard?.cardSelected?.backgroundColor,
    ...theme.paymentOption?.selectedPayment,
  },
  checkMark: {
    position: "absolute",
    top: 0,
    right: 0,
    "&> svg": {
      fontSize: "3rem",
    },
    ...theme?.addOnCard?.checkmark,
  },
  content: {
    textAlign: "left",
  },
  header: {
    padding: "0 5px 15px 0",
    width: "100%",
    textAlign: "left",
    display: "table-cell",
    textDecoration: theme.addOnCard?.header?.textDecoration,
    fontWeight: theme.addOnCard?.header?.fontWeight,
    color: theme.addOnCard?.header?.textColor,
  },
  box: {
    alignItems: "center",
    justifyContent: "center",
  },
  paymentOptionButton: {
    width: "180px",
    [theme.breakpoints.down(460)]: {
      width: "100%",
    },
  },
  paragraph: {
    color: theme.palette.text.primary,
    ...theme.addOnCard?.card?.content,
  },
  divider: {
    marginTop: ".5rem",
    marginBottom: ".5rem",
  },
  placeholder: {
    marginTop: ".5rem",
  },
  title: {
    color: theme.palette.primary.main,
  },
  formControl: {
    width: "100%",
  },
  bankSelect: {
    marginTop: "1rem",
  },
  wideItem: {
    width: "100%",
    wordBreak: "keep-all",
  },
  selectButton: {
    margin: 0,
    ...theme?.paymentOption?.selectButton,
  },
  input: {
    backgroundColor: "#fff",
  },
}));

const PaymentOption: React.FC<PaymentTypeProps> = (props: PaymentTypeProps) => {
  const bankRef = React.useRef<HTMLDivElement>(null);

  const handleBankChange = (value?: string) => {
    props.bankChanged({
      paymentCode: props.code,
      bankCode: value,
    } as IPaymentActionData);
  };

  const handlePaymentOptionChange = () => {
    props.paymentTypeChanged({
      paymentCode: props.code,
      bankCode: props.bankCode,
    } as IPaymentActionData);
  };

  const handlePeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.paymentPeriodChanged({
      paymentCode: event,
      bankCode: props.bankCode,
    });
  };

  const period = props.selectedPaymentTypeCode ?? "";
  const bank = props.selectedBankCode ?? "";

  const theme: Theme = useTheme();
  const classes = useStyles(theme);
  const variant = "h5";

  if (props.showError) {
    if (bankRef.current) {
      bankRef.current.scrollIntoView();
    }
  }

  const checkmark =
    props.selected &&
    ((theme.icons?.checkmarkSelectedOptionIcon && <SvgIcon fontSize='large'>{theme.icons?.checkmarkSelectedOptionIcon}</SvgIcon>) ?? (
      <CheckMark />
    ));

  const add = (theme.icons?.addIcon && <SvgIcon color='primary'>{theme.icons?.addIcon}</SvgIcon>) ?? (
    <AddCircle color='primary' htmlColor={theme.palette.text.secondary} />
  );
  const remove = (theme.icons?.removeIcon && <SvgIcon color='primary'>{theme.icons?.removeIcon}</SvgIcon>) ?? (
    <ClearRoundedIcon color='primary' />
  );

  useLocalValidation({
    fieldName: "bankCode",
    state: props.code === "2EF" && props.selected && props.showError ? 1 : 0,
    value: props.selectedBankCode,
    errorMessage: "",
  });

  return (
    <Box mb={1} component='div' className={classes.box} display='flex'>
      <Card className={props.selected ? classes.cardSelected : classes.card} variant='outlined'>
        {props.selected && <Box className={classes.checkMark}>{checkmark}</Box>}
        <CardContent className={classes.content}>
          <div className={classes.title}>
            <Typography variant={variant} className={classes.header}>
              {props.title}
            </Typography>
            {!props.selected && (
              <CardActions className={[classes.content, classes.header].join(" ")}>
                <FormControlLabel
                  className={classes.selectButton}
                  control={<Checkbox checkedIcon={remove} icon={add} checked={props.selected} onChange={handlePaymentOptionChange} />}
                  label={"Välj"}
                />
              </CardActions>
            )}
          </div>
          <Typography component='p' className={classes.paragraph}>
            {props.description}
          </Typography>
          {props.secondaryText && props.selected && (
            <div>
              <Divider className={classes.divider} />
              <Typography className={classes.label}>{props.secondaryText}</Typography>
            </div>
          )}
          {props.paymentPeriods && props.selected && (
            <div>
              <RadioButtonGroupBase
                onChange={handlePeriodChange}
                values={props.paymentPeriods.map(x => ({
                  value: x.Code,
                  title: x.Name,
                }))}
                selected={period}
                id='paymentOption'
                buttonStyle={classes.paymentOptionButton}
              />
            </div>
          )}
          {props.bankList && props.selected && (
            <div className={clsx(classes.placeholder, classes.wideItem)} ref={bankRef}>
              <Typography className={clsx(classes.dropdownLabel, props.showError && classes.errorMessage)}>
                {props.bankHelperText}
              </Typography>
              <FormControl className={classes.formControl}>
                <DropdownSelectorBase
                  label={{ id: "BankSelectionId" }}
                  dropdownId='BankSelectionId'
                  selected={bank}
                  showError={props.showError}
                  onChange={handleBankChange}
                  values={props.bankList?.map(i => ({
                    value: i.Code,
                    title: i.Name,
                  }))}
                />
              </FormControl>
            </div>
          )}
          {props.selected && props.code.includes("BGA") && <BankAccountInputsSection inputClass={clsx(classes.input)} />}
          {props.selected &&
            props.code.includes("12") &&
            props.digitalSigning &&
            props.digitalSigning.showMessage &&
            props.digitalSigning.message && (
              <div>
                <Typography className={classes.label}>{props.digitalSigning.message}</Typography>
              </div>
            )}
        </CardContent>
      </Card>
    </Box>
  );
};

type PaymentTypeProps = IPaymentOption & {
  paymentTypeChanged: Function;
  paymentPeriodChanged: Function;
  bankChanged: Function;

  selectedPaymentTypeCode?: string;
  selectedBankCode?: string;
  digitalSigning?: {
    showMessage?: boolean;
    message?: string;
  };
};

export default PaymentOption;
